import { useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { ProjectSettings } from 'shared/types/projectSettings/ProjectSettings';
import AddressToolTip from '@/components/customercenter-ui/tooltip/address';
import InputField from '@/customercenter-ui/common/validateForm/inputField';
import { UserSchema } from '@/customercenter-ui/common/validateForm/schema';
import { StateDropDown } from '@/customercenter-ui/common/validateForm/stateDropDown';
import AddCircle from '@/customercenter-ui/icons/addCircle';
import WarningIcon from '@/customercenter-ui/svgs/notifications/warning';
import ToolTips from '@/customercenter-ui/tooltip';
import { Button } from '@/customercenter-ui/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/customercenter-ui/ui/dialog';
import { Label } from '@/customercenter-ui/ui/label';
import { RadioGroup, RadioGroupItem } from '@/customercenter-ui/ui/radio-group';
import { maskPostalCode } from '@/customercenter-ui/utils/formatUtils';
import { useProjSettings } from '@/frontastic/hooks/useProjectSettings/useProjectSettings';
import { btnHoverClass, primaryBtnClass } from '@/helpers/utils/brandButton';
import { mapLocaleToMeaningfulFormat } from '@/helpers/utils/i18n';
import { getSelectedSite } from '@/helpers/utils/siteUtils';
import { DifferenceHighlighter } from './differenceHighlighter';

function setSelectAddressValue(validateAddressResponse: any) {
  if (validateAddressResponse?.result === 'premises partial' || validateAddressResponse?.result === 'no matches') {
    return mapLocaleToMeaningfulFormat().editLabel;
  } else if (validateAddressResponse?.result == 'multiple matches') {
    return mapLocaleToMeaningfulFormat().recommendLabel;
  } else {
    return mapLocaleToMeaningfulFormat().recommendLabel;
  }
}

function setInteractionRequiredLocaiton(validateAddressResponse: any) {
  if (
    validateAddressResponse?.result === 'interaction required' &&
    validateAddressResponse?.ValidatedAddress?.LocationInfo
  ) {
    return validateAddressResponse?.ValidatedAddress?.LocationInfo;
  }
}

export function ValidateAddressModal({
  openModal,
  setOpenModal,
  editedAddress,
  values,
  validateAddressResponse,
  mergeDataWithFormData,
  setFormattingKey,
  formattingKey,
  setIsLoading = () => {},
}: any) {
  const [selectAddress, setSelectAddress] = useState(setSelectAddressValue(validateAddressResponse));
  const [showadditionalAddressInfo, setShowadditionalAddressInfo] = useState(
    values?.additionalAddressInfo ? true : false,
  );
  const projectSettings: ProjectSettings = useProjSettings();
  const brandName = projectSettings?.brandName;
  const siteId = getSelectedSite() ?? 'ZBSite';
  const [postalCode, setPostalCode] = useState(values?.postalCode);
  const interactionResponse = setInteractionRequiredLocaiton(validateAddressResponse);

  const {
    handleSubmit,
    register,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(UserSchema),
    defaultValues: values
      ? {
          firstName: values?.firstName,
          lastName: values?.lastName,
          streetName: values?.streetName,
          additionalAddressInfo: values?.additionalAddressInfo,
          state: values?.state,
          city: values?.city,
          postalCode: values?.postalCode,
        }
      : {
          checkDefaultAddress: false,
        },
    mode: 'onBlur',
  });

  const handleShowAdditionalAddressInfo = () => {
    setShowadditionalAddressInfo(true);
  };

  const handlePostalCodeInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPostalCode(maskPostalCode(event.target.value));
  };
  const handleRadioChange = (value: string) => {
    setSelectAddress(value);
  };

  const onSubmitModal = async (data: any) => {
    setOpenModal(false);
    // This is set toinvoke
    if (selectAddress === mapLocaleToMeaningfulFormat().editLabel) {
      data.isEditSelectedFromModel = true;
    }
    if (selectAddress === mapLocaleToMeaningfulFormat().recommendLabel) {
      if (interactionResponse) {
        mergeAddressnteractionReqWithSingle(interactionResponse, data);
      } else {
        data.isSuggestedAddressFromModel = true;
        data.formattingKey = formattingKey;
      }
    }
    data.isQasAddressEdited = true;
    data.country = 'US';
    mergeDataWithFormData(data);
  };

  const mergeAddressnteractionReqWithSingle = (locationInfo: any, data: any) => {
    data.streetName = locationInfo?.line1;
    data.additionalAddressInfo = locationInfo?.line2;
    data.city = locationInfo.city;
    data.state = locationInfo.state;
    data.postalCode = locationInfo.zip;
  };

  return (
    <Dialog
      open={openModal}
      onOpenChange={(value) => {
        if (!value) {
          setOpenModal?.(false);
          setIsLoading?.(false);
        } else {
          setOpenModal?.(true);
        }
      }}
    >
      <DialogContent className="max-h-[calc(100vh_-_40px)] max-w-[400px] overflow-y-scroll [-ms-overflow-style:none] [scrollbar-width:none] lg:max-w-[529px] [&::-webkit-scrollbar]:hidden">
        <DialogHeader>
          <DialogTitle className="text-left text-xl font-bold">
            {mapLocaleToMeaningfulFormat().verifyAddress}
          </DialogTitle>

          {validateAddressResponse?.result === 'premises partial' ? (
            <DialogDescription className="pb-4 pt-2 text-left">
              {mapLocaleToMeaningfulFormat().partialPremisesDescription}
            </DialogDescription>
          ) : validateAddressResponse?.result === 'street partial' ? (
            <DialogDescription className="pb-4 pt-2 text-left">
              {mapLocaleToMeaningfulFormat().streetPartialDescription}
            </DialogDescription>
          ) : validateAddressResponse?.result === 'no matches' ? (
            <DialogDescription className="pb-4 pt-2 text-left">
              <strong>{mapLocaleToMeaningfulFormat().noMatchDescription}</strong>
              {mapLocaleToMeaningfulFormat().noMatchDescription1}
            </DialogDescription>
          ) : validateAddressResponse?.result === 'multiple matches' ||
            validateAddressResponse?.result === 'interaction required' ? (
            <DialogDescription className="pb-4 pt-2 text-left">
              {mapLocaleToMeaningfulFormat().verifyAddressDescription}
              <strong>{mapLocaleToMeaningfulFormat().verifyAddressDescription1}</strong>
              {mapLocaleToMeaningfulFormat().verifyAddressDescription2}
            </DialogDescription>
          ) : (
            <></>
          )}
        </DialogHeader>
        <form onSubmit={handleSubmit(onSubmitModal)} autoComplete="off">
          {validateAddressResponse?.result === 'premises partial' ? (
            <section>
              {validateAddressResponse?.SuggestedAddresses?.length > 1 ? (
                <RadioGroup defaultValue={selectAddress} className="gap-5">
                  <section className="flex items-center space-x-2">
                    <RadioGroupItem
                      value="edit"
                      id="r1"
                      onClick={() => handleRadioChange(mapLocaleToMeaningfulFormat().editLabel)}
                    />
                    <Label htmlFor="r1">
                      <section className="flex flex-col lg:flex-row lg:items-baseline">
                        <div className="lg:flex lg:flex-col">
                          <p className="text-base font-semibold">{mapLocaleToMeaningfulFormat().editYourAddress}</p>
                          <p className="text-xs font-normal">{mapLocaleToMeaningfulFormat().requiredFields}</p>
                        </div>
                        <p className="font-xs font-normal text-grayDark lg:pl-1">
                          {mapLocaleToMeaningfulFormat().recommended}
                        </p>
                      </section>
                    </Label>
                  </section>
                  {selectAddress === mapLocaleToMeaningfulFormat().editLabel ? (
                    <>
                      <section className="mt-2 flex max-w-[356px] items-center gap-2 bg-almond py-3 text-sm font-medium text-black lg:ml-6">
                        <WarningIcon className="ml-3 h-5 w-5" />
                        {mapLocaleToMeaningfulFormat().verifyEditAddressAlert}
                      </section>
                      <section>
                        <section className="flex w-full gap-x-2 pt-2 lg:px-5">
                          <section className="w-full lg:max-w-[398px]">
                            <InputField
                              items={mapLocaleToMeaningfulFormat().addressLine1}
                              id={
                                editedAddress
                                  ? mapLocaleToMeaningfulFormat().editAddressId
                                  : mapLocaleToMeaningfulFormat().addressId
                              }
                              htmlFor={
                                editedAddress
                                  ? mapLocaleToMeaningfulFormat().editAddressId
                                  : mapLocaleToMeaningfulFormat().addressId
                              }
                              register={register}
                              errors={errors}
                              label="streetName"
                              classname="w-full address-input"
                              maxlength={30}
                            />
                          </section>
                          <ToolTips className="mt-5" Content={{ ContentToolTip: <AddressToolTip /> }} />
                        </section>
                        {!showadditionalAddressInfo && (
                          <button
                            type="button"
                            className="flex items-center gap-x-2 pb-5 lg:px-5"
                            onClick={handleShowAdditionalAddressInfo}
                          >
                            <AddCircle className="h-5 w-5" />
                            <p className="text-base font-normal leading-5">
                              {mapLocaleToMeaningfulFormat().addressLine2}
                            </p>
                            <p className="text-sm font-normal leading-5">
                              ({mapLocaleToMeaningfulFormat().optionalText})
                            </p>
                          </button>
                        )}
                        <section
                          className={`${
                            !showadditionalAddressInfo ? 'hidden ' : 'block'
                          } flex w-full items-center pt-2 lg:px-5`}
                        >
                          <section className="w-full lg:max-w-[398px]">
                            <InputField
                              items={mapLocaleToMeaningfulFormat().addressLine2}
                              id={
                                editedAddress
                                  ? mapLocaleToMeaningfulFormat().editAddressLine2
                                  : mapLocaleToMeaningfulFormat().additionalAddressInfo
                              }
                              htmlFor={
                                editedAddress
                                  ? mapLocaleToMeaningfulFormat().editAddressLine2
                                  : mapLocaleToMeaningfulFormat().additionalAddressInfo
                              }
                              register={register}
                              errors={errors}
                              label="additionalAddressInfo"
                              classname="w-full addressLine2"
                              maxlength={30}
                            />
                          </section>
                        </section>
                        <section className="flex w-full items-center lg:px-5">
                          <section className="w-full lg:max-w-[398px]">
                            <InputField
                              items={mapLocaleToMeaningfulFormat().city}
                              id={
                                editedAddress
                                  ? mapLocaleToMeaningfulFormat().editCity
                                  : mapLocaleToMeaningfulFormat().cityId
                              }
                              htmlFor={
                                editedAddress
                                  ? mapLocaleToMeaningfulFormat().editCity
                                  : mapLocaleToMeaningfulFormat().cityId
                              }
                              register={register}
                              errors={errors}
                              label="city"
                              classname="w-full city"
                              maxlength={30}
                            />
                          </section>
                        </section>
                        <section className="flex gap-x-5 pt-2 lg:w-full lg:px-5">
                          <section className="w-full lg:max-w-[217px]">
                            <StateDropDown
                              setValue={setValue}
                              errors={errors}
                              label="state"
                              classname="w-full state"
                              initialState={values?.state}
                              register={register}
                              id="state"
                            />
                          </section>
                          <section className="w-full max-w-[136px] lg:max-w-[161px]">
                            <InputField
                              items={mapLocaleToMeaningfulFormat().zipCode}
                              id={
                                editedAddress
                                  ? mapLocaleToMeaningfulFormat().editZipCode
                                  : mapLocaleToMeaningfulFormat().zipCodeId
                              }
                              htmlFor={
                                editedAddress
                                  ? mapLocaleToMeaningfulFormat().editZipCode
                                  : mapLocaleToMeaningfulFormat().zipCodeId
                              }
                              register={register}
                              errors={errors}
                              label="postalCode"
                              maxlength={10}
                              classname="w-full zip"
                              value={postalCode}
                              onChange={handlePostalCodeInputChange}
                            />
                          </section>
                        </section>
                      </section>
                    </>
                  ) : (
                    <></>
                  )}

                  <section className="flex items-center space-x-2">
                    <RadioGroupItem
                      value="recommended"
                      id="r2"
                      onClick={() => handleRadioChange(mapLocaleToMeaningfulFormat().recommendLabel)}
                    />
                    <Label htmlFor="r2">
                      <p className="text-base font-semibold">{mapLocaleToMeaningfulFormat().selectVerifiedAddress}</p>
                    </Label>
                  </section>
                  {selectAddress === mapLocaleToMeaningfulFormat().recommendLabel ? (
                    <section className="ml-6">
                      <p className="text-base font-normal">
                        {mapLocaleToMeaningfulFormat().suggestedChanges}{' '}
                        <strong>{mapLocaleToMeaningfulFormat().bold}</strong>
                      </p>
                      <RadioGroup className="gap-5">
                        {validateAddressResponse?.SuggestedAddresses?.map((element: any) => (
                          <>
                            <section className="flex items-center space-x-2">
                              <RadioGroupItem
                                value={element.suggestionAddressKey}
                                id={element.suggestionAddressKey}
                                onClick={() => setFormattingKey(element.suggestionAddressKey)}
                              />
                              <section className="pb-3">
                                <DifferenceHighlighter
                                  string1={element?.suggestionAddress}
                                  string2={
                                    values.streetName +
                                    ' ' +
                                    values.additionalAddressInfo +
                                    ' ' +
                                    values.city +
                                    ' ' +
                                    values.state
                                  }
                                />
                                <DifferenceHighlighter string1={element?.suggestionZip} string2={values.postalCode} />
                              </section>
                            </section>
                          </>
                        ))}
                      </RadioGroup>
                    </section>
                  ) : (
                    <></>
                  )}

                  <section className="flex items-center space-x-2">
                    <RadioGroupItem
                      value="original"
                      id="r3"
                      onClick={() => handleRadioChange(mapLocaleToMeaningfulFormat().originalLabel)}
                    />
                    <Label htmlFor="r3" className="text-base font-semibold">
                      {mapLocaleToMeaningfulFormat().originalAddress}
                    </Label>
                  </section>
                  {selectAddress === mapLocaleToMeaningfulFormat().originalLabel ? (
                    <section className="ml-6">
                      <section className="mt-2 flex max-w-[356px] items-center gap-2 bg-almond py-3 text-sm font-medium text-black">
                        <WarningIcon className="ml-3 h-5 w-5" />
                        {mapLocaleToMeaningfulFormat().originalAddressAlert}
                      </section>
                      <section className="pt-5">
                        <p>{values?.streetName}</p>
                        <p> {values?.additionalAddressInfo}</p>
                        <p>{values?.city}</p>
                        <p>
                          {' '}
                          {values?.state} {values?.postalCode}
                        </p>
                      </section>
                    </section>
                  ) : (
                    <></>
                  )}
                </RadioGroup>
              ) : (
                <RadioGroup defaultValue={selectAddress} className="gap-5">
                  <section className="flex items-center space-x-2">
                    <RadioGroupItem
                      value="edit"
                      id="r1"
                      onClick={() => handleRadioChange(mapLocaleToMeaningfulFormat().editLabel)}
                    />
                    <Label htmlFor="r1">
                      <p className="text-base font-semibold">{mapLocaleToMeaningfulFormat().editYourAddress}</p>
                      {selectAddress === mapLocaleToMeaningfulFormat().editLabel ? (
                        <>
                          <p className="text-xs font-normal">{mapLocaleToMeaningfulFormat().requiredFields}</p>
                          <p className="font-xs font-normal text-grayDark lg:pl-1">
                            {mapLocaleToMeaningfulFormat().recommended}
                          </p>
                        </>
                      ) : (
                        <></>
                      )}
                    </Label>
                  </section>
                  {selectAddress === mapLocaleToMeaningfulFormat().editLabel ? (
                    <>
                      <section className="mt-2 flex max-w-[356px] items-center gap-2 bg-almond py-3 text-sm font-medium text-black lg:ml-6">
                        <WarningIcon className="ml-3 h-5 w-5" />
                        {mapLocaleToMeaningfulFormat().verifyEditAddressAlert}
                      </section>
                      <section>
                        <section className="flex w-full gap-x-2 pt-2 lg:px-5">
                          <section className="w-full lg:max-w-[398px]">
                            <InputField
                              items={mapLocaleToMeaningfulFormat().addressLine1}
                              id={
                                editedAddress
                                  ? mapLocaleToMeaningfulFormat().editAddressId
                                  : mapLocaleToMeaningfulFormat().addressId
                              }
                              htmlFor={
                                editedAddress
                                  ? mapLocaleToMeaningfulFormat().editAddressId
                                  : mapLocaleToMeaningfulFormat().addressId
                              }
                              register={register}
                              errors={errors}
                              label="streetName"
                              classname="w-full address-input"
                              maxlength={30}
                            />
                          </section>
                          <ToolTips className="mt-5" Content={{ ContentToolTip: <AddressToolTip /> }} />
                        </section>
                        {!showadditionalAddressInfo && (
                          <button
                            type="button"
                            className="flex items-center gap-x-2 pb-5 lg:px-5"
                            onClick={handleShowAdditionalAddressInfo}
                          >
                            <AddCircle className="h-5 w-5" />
                            <p className="text-base font-normal leading-5">
                              {mapLocaleToMeaningfulFormat().addressLine2}
                            </p>
                            <p className="text-sm font-normal leading-5">
                              ({mapLocaleToMeaningfulFormat().optionalText})
                            </p>
                          </button>
                        )}
                        <section
                          className={`${
                            !showadditionalAddressInfo ? 'hidden ' : 'block'
                          } flex w-full items-center pt-2 lg:px-5`}
                        >
                          <section className="w-full lg:max-w-[398px]">
                            <InputField
                              items={mapLocaleToMeaningfulFormat().addressLine2}
                              id={
                                editedAddress
                                  ? mapLocaleToMeaningfulFormat().editAddressLine2
                                  : mapLocaleToMeaningfulFormat().additionalAddressInfo
                              }
                              htmlFor={
                                editedAddress
                                  ? mapLocaleToMeaningfulFormat().editAddressLine2
                                  : mapLocaleToMeaningfulFormat().additionalAddressInfo
                              }
                              register={register}
                              errors={errors}
                              label="additionalAddressInfo"
                              classname="w-full addressLine2"
                              maxlength={30}
                            />
                          </section>
                        </section>
                        <section className="flex w-full items-center lg:px-5">
                          <section className="w-full lg:max-w-[398px]">
                            <InputField
                              items={mapLocaleToMeaningfulFormat().city}
                              id={
                                editedAddress
                                  ? mapLocaleToMeaningfulFormat().editCity
                                  : mapLocaleToMeaningfulFormat().cityId
                              }
                              htmlFor={
                                editedAddress
                                  ? mapLocaleToMeaningfulFormat().editCity
                                  : mapLocaleToMeaningfulFormat().cityId
                              }
                              register={register}
                              errors={errors}
                              label="city"
                              classname="w-full city"
                              maxlength={30}
                            />
                          </section>
                        </section>
                        <section className="flex gap-x-5 pt-2 lg:w-full lg:px-5">
                          <section className="w-full lg:max-w-[217px]">
                            <StateDropDown
                              setValue={setValue}
                              errors={errors}
                              label="state"
                              classname="w-full state"
                              initialState={values?.state}
                              register={register}
                              id="state"
                            />
                          </section>
                          <section className="w-full max-w-[136px] lg:max-w-[161px]">
                            <InputField
                              items={mapLocaleToMeaningfulFormat().zipCode}
                              id={
                                editedAddress
                                  ? mapLocaleToMeaningfulFormat().editZipCode
                                  : mapLocaleToMeaningfulFormat().zipCodeId
                              }
                              htmlFor={
                                editedAddress
                                  ? mapLocaleToMeaningfulFormat().editZipCode
                                  : mapLocaleToMeaningfulFormat().zipCodeId
                              }
                              register={register}
                              errors={errors}
                              label="postalCode"
                              maxlength={10}
                              classname="w-full zip"
                              value={postalCode}
                              onChange={handlePostalCodeInputChange}
                            />
                          </section>
                        </section>
                      </section>
                    </>
                  ) : (
                    <></>
                  )}

                  <section className="flex items-center space-x-2">
                    <RadioGroupItem
                      value="original"
                      id="r2"
                      onClick={() => handleRadioChange(mapLocaleToMeaningfulFormat().originalLabel)}
                    />
                    <Label htmlFor="r2" className="text-base font-semibold">
                      {mapLocaleToMeaningfulFormat().originalAddress}
                    </Label>
                  </section>
                  {selectAddress === mapLocaleToMeaningfulFormat().originalLabel ? (
                    <section className="ml-6">
                      <section className="mt-2 flex max-w-[356px] items-center gap-2 bg-almond py-3 text-sm font-medium text-black">
                        <WarningIcon className="ml-3 h-5 w-5" />
                        {mapLocaleToMeaningfulFormat().originalAddressAlert}
                      </section>
                      <section className="pt-5">
                        <p>{values?.streetName}</p>
                        <p> {values?.additionalAddressInfo}</p>
                        <p>{values?.city}</p>
                        <p>
                          {' '}
                          {values?.state} {values?.postalCode}
                        </p>
                      </section>
                    </section>
                  ) : (
                    <></>
                  )}
                </RadioGroup>
              )}
            </section>
          ) : validateAddressResponse?.result === 'street partial' ? (
            <>
              {validateAddressResponse?.SuggestedAddresses ? (
                <>
                  <section>
                    <RadioGroup defaultValue={selectAddress} className="gap-5">
                      <section className="flex items-center space-x-2">
                        <RadioGroupItem
                          value="recommended"
                          id="r1"
                          onClick={() => handleRadioChange(mapLocaleToMeaningfulFormat().recommendLabel)}
                        />
                        <Label htmlFor="r1">
                          <section className="flex flex-col lg:flex-row lg:items-center">
                            <p className="text-base font-semibold">{mapLocaleToMeaningfulFormat().suggestedAddress}</p>
                            <p className="font-xs font-normal text-grayDark lg:pl-1">
                              {mapLocaleToMeaningfulFormat().recommended}
                            </p>
                          </section>
                        </Label>
                      </section>
                      {selectAddress === mapLocaleToMeaningfulFormat().recommendLabel ? (
                        <section className="ml-6">
                          <RadioGroup className="gap-5">
                            {validateAddressResponse?.SuggestedAddresses?.map((element: any) => (
                              <>
                                <section className="flex items-center space-x-2">
                                  <RadioGroupItem
                                    value={element.suggestionAddressKey}
                                    id={element.suggestionAddressKey}
                                    onClick={() => setFormattingKey(element.suggestionAddressKey)}
                                  />
                                  <section className="pb-3">
                                    <DifferenceHighlighter
                                      string1={element?.suggestionAddress}
                                      string2={
                                        values.streetName +
                                        ' ' +
                                        values.additionalAddressInfo +
                                        ' ' +
                                        values.city +
                                        ' ' +
                                        values.state
                                      }
                                    />
                                    <DifferenceHighlighter
                                      string1={element?.suggestionZip}
                                      string2={values.postalCode}
                                    />
                                  </section>
                                </section>
                              </>
                            ))}
                          </RadioGroup>
                        </section>
                      ) : (
                        <></>
                      )}

                      <section className="flex items-center space-x-2">
                        <RadioGroupItem
                          value="edit"
                          id="r2"
                          onClick={() => handleRadioChange(mapLocaleToMeaningfulFormat().editLabel)}
                        />
                        <Label htmlFor="r2">
                          <p className="text-base font-semibold">{mapLocaleToMeaningfulFormat().editYourAddress}</p>
                          {selectAddress === mapLocaleToMeaningfulFormat().editLabel ? (
                            <p className="text-xs font-normal">{mapLocaleToMeaningfulFormat().requiredFields}</p>
                          ) : (
                            <></>
                          )}
                        </Label>
                      </section>
                      {selectAddress === mapLocaleToMeaningfulFormat().editLabel ? (
                        <>
                          <section className="mt-2 flex max-w-[356px] items-center gap-2 bg-almond py-3 text-sm font-medium text-black lg:ml-6">
                            <WarningIcon className="ml-3 h-5 w-5" />
                            {mapLocaleToMeaningfulFormat().verifyEditAddressAlert}
                          </section>
                          <section>
                            <section className="flex w-full gap-x-2 pt-2 lg:px-5">
                              <section className="w-full lg:max-w-[398px]">
                                <InputField
                                  items={mapLocaleToMeaningfulFormat().addressLine1}
                                  id={
                                    editedAddress
                                      ? mapLocaleToMeaningfulFormat().editAddressId
                                      : mapLocaleToMeaningfulFormat().addressId
                                  }
                                  htmlFor={
                                    editedAddress
                                      ? mapLocaleToMeaningfulFormat().editAddressId
                                      : mapLocaleToMeaningfulFormat().addressId
                                  }
                                  register={register}
                                  errors={errors}
                                  label="streetName"
                                  classname="w-full address-input"
                                  maxlength={30}
                                />
                              </section>
                              <ToolTips className="mt-5" Content={{ ContentToolTip: <AddressToolTip /> }} />
                            </section>
                            {!showadditionalAddressInfo && (
                              <button
                                type="button"
                                className="flex items-center gap-x-2 pb-5 lg:px-5"
                                onClick={handleShowAdditionalAddressInfo}
                              >
                                <AddCircle className="h-5 w-5" />
                                <p className="text-base font-normal leading-5">
                                  {mapLocaleToMeaningfulFormat().addressLine2}
                                </p>
                                <p className="text-sm font-normal leading-5">
                                  ({mapLocaleToMeaningfulFormat().optionalText})
                                </p>
                              </button>
                            )}
                            <section
                              className={`${
                                !showadditionalAddressInfo ? 'hidden ' : 'block'
                              } flex w-full items-center pt-2 lg:px-5`}
                            >
                              <section className="w-full lg:max-w-[398px]">
                                <InputField
                                  items={mapLocaleToMeaningfulFormat().addressLine2}
                                  id={
                                    editedAddress
                                      ? mapLocaleToMeaningfulFormat().editAddressLine2
                                      : mapLocaleToMeaningfulFormat().additionalAddressInfo
                                  }
                                  htmlFor={
                                    editedAddress
                                      ? mapLocaleToMeaningfulFormat().editAddressLine2
                                      : mapLocaleToMeaningfulFormat().additionalAddressInfo
                                  }
                                  register={register}
                                  errors={errors}
                                  label="additionalAddressInfo"
                                  classname="w-full addressLine2"
                                  maxlength={30}
                                />
                              </section>
                            </section>
                            <section className="flex w-full items-center lg:px-5">
                              <section className="w-full lg:max-w-[398px]">
                                <InputField
                                  items={mapLocaleToMeaningfulFormat().city}
                                  id={
                                    editedAddress
                                      ? mapLocaleToMeaningfulFormat().editCity
                                      : mapLocaleToMeaningfulFormat().cityId
                                  }
                                  htmlFor={
                                    editedAddress
                                      ? mapLocaleToMeaningfulFormat().editCity
                                      : mapLocaleToMeaningfulFormat().cityId
                                  }
                                  register={register}
                                  errors={errors}
                                  label="city"
                                  classname="w-full city"
                                  maxlength={30}
                                />
                              </section>
                            </section>
                            <section className="flex gap-x-5 pt-2 lg:w-full lg:px-5">
                              <section className="w-full lg:max-w-[217px]">
                                <StateDropDown
                                  setValue={setValue}
                                  errors={errors}
                                  label="state"
                                  classname="w-full state"
                                  initialState={values?.state}
                                  register={register}
                                  id="state"
                                />
                              </section>
                              <section className="w-full max-w-[136px] lg:max-w-[161px]">
                                <InputField
                                  items={mapLocaleToMeaningfulFormat().zipCode}
                                  id={
                                    editedAddress
                                      ? mapLocaleToMeaningfulFormat().editZipCode
                                      : mapLocaleToMeaningfulFormat().zipCodeId
                                  }
                                  htmlFor={
                                    editedAddress
                                      ? mapLocaleToMeaningfulFormat().editZipCode
                                      : mapLocaleToMeaningfulFormat().zipCodeId
                                  }
                                  register={register}
                                  errors={errors}
                                  label="postalCode"
                                  maxlength={10}
                                  classname="w-full zip"
                                  value={postalCode}
                                  onChange={handlePostalCodeInputChange}
                                />
                              </section>
                            </section>
                          </section>
                        </>
                      ) : (
                        <></>
                      )}

                      <section className="flex items-center space-x-2">
                        <RadioGroupItem
                          value="original"
                          id="r3"
                          onClick={() => handleRadioChange(mapLocaleToMeaningfulFormat().originalLabel)}
                        />
                        <Label htmlFor="r3" className="text-base font-semibold">
                          {mapLocaleToMeaningfulFormat().originalAddress}
                        </Label>
                      </section>
                      {selectAddress === mapLocaleToMeaningfulFormat().originalLabel ? (
                        <section className="ml-6">
                          <section className="mt-2 flex max-w-[356px] items-center gap-2 bg-almond py-3 text-sm font-medium text-black">
                            <WarningIcon className="ml-3 h-5 w-5" />
                            {mapLocaleToMeaningfulFormat().originalAddressAlert}
                          </section>
                          <section className="pt-5">
                            <p>{values?.streetName}</p>
                            <p> {values?.additionalAddressInfo}</p>
                            <p>{values?.city}</p>
                            <p>
                              {' '}
                              {values?.state} {values?.postalCode}
                            </p>
                          </section>
                        </section>
                      ) : (
                        <></>
                      )}
                    </RadioGroup>
                  </section>
                </>
              ) : (
                <>
                  <section>
                    <RadioGroup defaultValue={selectAddress} className="gap-5">
                      <section className="flex items-center space-x-2">
                        <RadioGroupItem
                          value="edit"
                          id="r1"
                          onClick={() => handleRadioChange(mapLocaleToMeaningfulFormat().recommendLabel)}
                        />
                        <Label htmlFor="r1">
                          <section className="flex flex-col lg:flex-row lg:items-baseline">
                            <div className="lg:flex lg:flex-col">
                              <p className="text-base font-semibold">{mapLocaleToMeaningfulFormat().editYourAddress}</p>
                              <p className="text-xs font-normal">{mapLocaleToMeaningfulFormat().requiredFields}</p>
                            </div>
                            <p className="font-xs font-normal text-grayDark lg:pl-1">
                              {mapLocaleToMeaningfulFormat().recommended}
                            </p>
                          </section>
                        </Label>
                      </section>
                      {selectAddress === mapLocaleToMeaningfulFormat().editLabel ? (
                        <>
                          <section className="mt-2 flex max-w-[356px] items-center gap-2 bg-almond py-3 text-sm font-medium text-black lg:ml-6">
                            <WarningIcon className="ml-3 h-5 w-5" />
                            {mapLocaleToMeaningfulFormat().verifyEditAddressAlert}
                          </section>
                          <section>
                            <section className="flex w-full gap-x-2 pt-2 lg:px-5">
                              <section className="w-full lg:max-w-[398px]">
                                <InputField
                                  items={mapLocaleToMeaningfulFormat().addressLine1}
                                  id={
                                    editedAddress
                                      ? mapLocaleToMeaningfulFormat().editAddressId
                                      : mapLocaleToMeaningfulFormat().addressId
                                  }
                                  htmlFor={
                                    editedAddress
                                      ? mapLocaleToMeaningfulFormat().editAddressId
                                      : mapLocaleToMeaningfulFormat().addressId
                                  }
                                  register={register}
                                  errors={errors}
                                  label="streetName"
                                  classname="w-full address-input"
                                  maxlength={30}
                                />
                              </section>
                              <ToolTips className="mt-5" Content={{ ContentToolTip: <AddressToolTip /> }} />
                            </section>
                            {!showadditionalAddressInfo && (
                              <button
                                type="button"
                                className="flex items-center gap-x-2 pb-5 lg:px-5"
                                onClick={handleShowAdditionalAddressInfo}
                              >
                                <AddCircle className="h-5 w-5" />
                                <p className="text-base font-normal leading-5">
                                  {mapLocaleToMeaningfulFormat().addressLine2}
                                </p>
                                <p className="text-sm font-normal leading-5">
                                  ({mapLocaleToMeaningfulFormat().optionalText})
                                </p>
                              </button>
                            )}
                            <section
                              className={`${
                                !showadditionalAddressInfo ? 'hidden ' : 'block'
                              } flex w-full items-center pt-2 lg:px-5`}
                            >
                              <section className="w-full lg:max-w-[398px]">
                                <InputField
                                  items={mapLocaleToMeaningfulFormat().addressLine2}
                                  id={
                                    editedAddress
                                      ? mapLocaleToMeaningfulFormat().editAddressLine2
                                      : mapLocaleToMeaningfulFormat().additionalAddressInfo
                                  }
                                  htmlFor={
                                    editedAddress
                                      ? mapLocaleToMeaningfulFormat().editAddressLine2
                                      : mapLocaleToMeaningfulFormat().additionalAddressInfo
                                  }
                                  register={register}
                                  errors={errors}
                                  label="additionalAddressInfo"
                                  classname="w-full addressLine2"
                                  maxlength={30}
                                />
                              </section>
                            </section>
                            <section className="flex w-full items-center lg:px-5">
                              <section className="w-full lg:max-w-[398px]">
                                <InputField
                                  items={mapLocaleToMeaningfulFormat().city}
                                  id={
                                    editedAddress
                                      ? mapLocaleToMeaningfulFormat().editCity
                                      : mapLocaleToMeaningfulFormat().cityId
                                  }
                                  htmlFor={
                                    editedAddress
                                      ? mapLocaleToMeaningfulFormat().editCity
                                      : mapLocaleToMeaningfulFormat().cityId
                                  }
                                  register={register}
                                  errors={errors}
                                  label="city"
                                  classname="w-full city"
                                  maxlength={30}
                                />
                              </section>
                            </section>
                            <section className="flex gap-x-5 pt-2 lg:w-full lg:px-5">
                              <section className="w-full lg:max-w-[217px]">
                                <StateDropDown
                                  setValue={setValue}
                                  errors={errors}
                                  label="state"
                                  classname="w-full state"
                                  initialState={values?.state}
                                  register={register}
                                  id="state"
                                />
                              </section>
                              <section className="w-full max-w-[136px] lg:max-w-[161px]">
                                <InputField
                                  items={mapLocaleToMeaningfulFormat().zipCode}
                                  id={
                                    editedAddress
                                      ? mapLocaleToMeaningfulFormat().editZipCode
                                      : mapLocaleToMeaningfulFormat().zipCodeId
                                  }
                                  htmlFor={
                                    editedAddress
                                      ? mapLocaleToMeaningfulFormat().editZipCode
                                      : mapLocaleToMeaningfulFormat().zipCodeId
                                  }
                                  register={register}
                                  errors={errors}
                                  label="postalCode"
                                  maxlength={10}
                                  classname="w-full zip"
                                  value={postalCode}
                                  onChange={handlePostalCodeInputChange}
                                />
                              </section>
                            </section>
                          </section>
                        </>
                      ) : (
                        <></>
                      )}

                      <section className="flex items-center space-x-2">
                        <RadioGroupItem
                          value="original"
                          id="r2"
                          onClick={() => handleRadioChange(mapLocaleToMeaningfulFormat().originalLabel)}
                        />
                        <Label htmlFor="r2" className="text-base font-semibold">
                          {mapLocaleToMeaningfulFormat().originalAddress}
                        </Label>
                      </section>
                      {selectAddress === mapLocaleToMeaningfulFormat().originalLabel ? (
                        <section className="ml-6">
                          <section className="mt-2 flex max-w-[356px] items-center gap-2 bg-almond py-3 text-sm font-medium text-black">
                            <WarningIcon className="ml-3 h-5 w-5" />
                            {mapLocaleToMeaningfulFormat().originalAddressAlert}
                          </section>
                          <section className="pt-5">
                            <p>{values?.streetName}</p>
                            <p> {values?.additionalAddressInfo}</p>
                            <p>{values?.city}</p>
                            <p>
                              {' '}
                              {values?.state} {values?.postalCode}
                            </p>
                          </section>
                        </section>
                      ) : (
                        <></>
                      )}
                    </RadioGroup>
                  </section>
                </>
              )}
            </>
          ) : validateAddressResponse?.result === 'no matches' ? (
            <section>
              <RadioGroup defaultValue={selectAddress} className="gap-5">
                <section className="flex items-center space-x-2">
                  <RadioGroupItem
                    value="edit"
                    id="r1"
                    onClick={() => handleRadioChange(mapLocaleToMeaningfulFormat().editLabel)}
                  />
                  <Label htmlFor="r1">
                    <section className="flex flex-col lg:flex-row lg:items-baseline">
                      <div className="lg:flex lg:flex-col">
                        <p className="text-base font-semibold">{mapLocaleToMeaningfulFormat().editYourAddress}</p>
                        <p className="text-xs font-normal">{mapLocaleToMeaningfulFormat().requiredFields}</p>
                      </div>
                      <p className="font-xs font-normal text-grayDark lg:pl-1">
                        {mapLocaleToMeaningfulFormat().recommended}
                      </p>
                    </section>
                  </Label>
                </section>
                {selectAddress === mapLocaleToMeaningfulFormat().editLabel ? (
                  <>
                    <section className="mt-2 flex max-w-[356px] items-center gap-2  bg-almond py-3 text-sm font-medium text-black lg:ml-6">
                      <WarningIcon className="ml-3 h-5 w-5" />
                      {mapLocaleToMeaningfulFormat().verifyEditAddressAlert}
                    </section>
                    <section>
                      <section className="flex w-full gap-x-2 pt-2 lg:px-5">
                        <section className="w-full lg:max-w-[398px]">
                          <InputField
                            items={mapLocaleToMeaningfulFormat().addressLine1}
                            id={
                              editedAddress
                                ? mapLocaleToMeaningfulFormat().editAddressId
                                : mapLocaleToMeaningfulFormat().addressId
                            }
                            htmlFor={
                              editedAddress
                                ? mapLocaleToMeaningfulFormat().editAddressId
                                : mapLocaleToMeaningfulFormat().addressId
                            }
                            register={register}
                            errors={errors}
                            label="streetName"
                            classname="w-full address-input"
                            maxlength={30}
                          />
                        </section>
                        <ToolTips className="mt-5" Content={{ ContentToolTip: <AddressToolTip /> }} />
                      </section>
                      {!showadditionalAddressInfo && (
                        <button
                          type="button"
                          className="flex items-center gap-x-2 pb-5 lg:px-5"
                          onClick={handleShowAdditionalAddressInfo}
                        >
                          <AddCircle className="h-5 w-5" />
                          <p className="text-base font-normal leading-5">
                            {mapLocaleToMeaningfulFormat().addressLine2}
                          </p>
                          <p className="text-sm font-normal leading-5">
                            ({mapLocaleToMeaningfulFormat().optionalText})
                          </p>
                        </button>
                      )}
                      <section
                        className={`${
                          !showadditionalAddressInfo ? 'hidden ' : 'block'
                        } flex w-full items-center pt-2 lg:px-5`}
                      >
                        <section className="w-full lg:max-w-[398px]">
                          <InputField
                            items={mapLocaleToMeaningfulFormat().addressLine2}
                            id={
                              editedAddress
                                ? mapLocaleToMeaningfulFormat().editAddressLine2
                                : mapLocaleToMeaningfulFormat().additionalAddressInfo
                            }
                            htmlFor={
                              editedAddress
                                ? mapLocaleToMeaningfulFormat().editAddressLine2
                                : mapLocaleToMeaningfulFormat().additionalAddressInfo
                            }
                            register={register}
                            errors={errors}
                            label="additionalAddressInfo"
                            classname="w-full addressLine2"
                            maxlength={30}
                          />
                        </section>
                      </section>
                      <section className="flex w-full items-center lg:px-5">
                        <section className="w-full lg:max-w-[398px]">
                          <InputField
                            items={mapLocaleToMeaningfulFormat().city}
                            id={
                              editedAddress
                                ? mapLocaleToMeaningfulFormat().editCity
                                : mapLocaleToMeaningfulFormat().cityId
                            }
                            htmlFor={
                              editedAddress
                                ? mapLocaleToMeaningfulFormat().editCity
                                : mapLocaleToMeaningfulFormat().cityId
                            }
                            register={register}
                            errors={errors}
                            label="city"
                            classname="w-full city"
                            maxlength={30}
                          />
                        </section>
                      </section>
                      <section className="flex gap-x-5 pt-2 lg:w-full lg:px-5">
                        <section className="w-full lg:max-w-[217px]">
                          <StateDropDown
                            setValue={setValue}
                            errors={errors}
                            label="state"
                            classname="w-full state"
                            initialState={values?.state}
                            register={register}
                            id="state"
                          />
                        </section>
                        <section className="w-full max-w-[136px] lg:max-w-[161px]">
                          <InputField
                            items={mapLocaleToMeaningfulFormat().zipCode}
                            id={
                              editedAddress
                                ? mapLocaleToMeaningfulFormat().editZipCode
                                : mapLocaleToMeaningfulFormat().zipCodeId
                            }
                            htmlFor={
                              editedAddress
                                ? mapLocaleToMeaningfulFormat().editZipCode
                                : mapLocaleToMeaningfulFormat().zipCodeId
                            }
                            register={register}
                            errors={errors}
                            label="postalCode"
                            maxlength={10}
                            classname="w-full zip"
                            value={postalCode}
                            onChange={handlePostalCodeInputChange}
                          />
                        </section>
                      </section>
                    </section>
                  </>
                ) : (
                  <></>
                )}

                <section className="flex items-center space-x-2">
                  <RadioGroupItem
                    value="original"
                    id="r2"
                    onClick={() => handleRadioChange(mapLocaleToMeaningfulFormat().originalLabel)}
                  />
                  <Label htmlFor="r2" className="text-base font-semibold">
                    {mapLocaleToMeaningfulFormat().originalAddress}
                  </Label>
                </section>
                {selectAddress === mapLocaleToMeaningfulFormat().originalLabel ? (
                  <section className="ml-6">
                    <section className="mt-2 flex max-w-[356px] items-center gap-2 bg-almond py-3 text-sm font-medium text-black">
                      <WarningIcon className="ml-3 h-5 w-5" />
                      {mapLocaleToMeaningfulFormat().originalAddressAlert}
                    </section>
                    <section className="pt-5">
                      <p>{values?.streetName}</p>
                      <p> {values?.additionalAddressInfo}</p>
                      <p>{values?.city}</p>
                      <p>
                        {' '}
                        {values?.state} {values?.postalCode}
                      </p>
                    </section>
                  </section>
                ) : (
                  <></>
                )}
              </RadioGroup>
            </section>
          ) : validateAddressResponse?.result === 'multiple matches' ||
            validateAddressResponse?.result === 'interaction required' ? (
            <section>
              <RadioGroup defaultValue={selectAddress} className="gap-5">
                <section className="flex items-center space-x-2">
                  <RadioGroupItem
                    value="recommended"
                    id="r1"
                    onClick={() => handleRadioChange(mapLocaleToMeaningfulFormat().recommendLabel)}
                  />
                  <Label htmlFor="r1">
                    <section className="flex flex-col lg:flex-row lg:items-center">
                      <p className="text-base font-semibold">{mapLocaleToMeaningfulFormat().suggestedAddress}</p>
                      <p className="font-xs font-normal text-grayDark lg:pl-1">
                        {mapLocaleToMeaningfulFormat().recommended}
                      </p>
                    </section>
                  </Label>
                </section>
                {selectAddress === mapLocaleToMeaningfulFormat().recommendLabel ? (
                  <section className="ml-6">
                    {validateAddressResponse?.result === 'interaction required' &&
                    validateAddressResponse?.ValidatedAddress?.LocationInfo ? (
                      <>
                        <section className="flex items-center space-x-2">
                          <section className="pb-3">
                            <DifferenceHighlighter
                              string1={
                                validateAddressResponse.ValidatedAddress.LocationInfo?.line1 +
                                ' ' +
                                validateAddressResponse.ValidatedAddress.LocationInfo?.city +
                                ' ' +
                                validateAddressResponse.ValidatedAddress.LocationInfo?.state
                              }
                              string2={values.streetName + ' ' + values.city + ' ' + values.state}
                            />
                            <DifferenceHighlighter
                              string1={validateAddressResponse.ValidatedAddress.LocationInfo.zip}
                              string2={values.postalCode}
                            />
                          </section>
                        </section>
                      </>
                    ) : (
                      <>
                        {validateAddressResponse?.SuggestedAddresses?.map((element: any) => (
                          <>
                            <RadioGroup className="gap-5">
                              <section className="flex items-center space-x-2">
                                <RadioGroupItem
                                  value={element.suggestionAddressKey}
                                  id={element.suggestionAddressKey}
                                  onClick={() => setFormattingKey(element.suggestionAddressKey)}
                                />
                                <section className="pb-3">
                                  <DifferenceHighlighter
                                    string1={element?.suggestionAddress}
                                    string2={
                                      values.streetName +
                                      ' ' +
                                      values.additionalAddressInfo +
                                      ' ' +
                                      values.city +
                                      ' ' +
                                      values.state
                                    }
                                  />
                                  <DifferenceHighlighter string1={element?.suggestionZip} string2={values.postalCode} />
                                </section>
                              </section>
                            </RadioGroup>
                          </>
                        ))}
                      </>
                    )}
                  </section>
                ) : (
                  <></>
                )}

                <section className="flex items-center space-x-2">
                  <RadioGroupItem
                    value="edit"
                    id="r2"
                    onClick={() => handleRadioChange(mapLocaleToMeaningfulFormat().editLabel)}
                  />
                  <Label htmlFor="r2">
                    <p className="text-base font-semibold">{mapLocaleToMeaningfulFormat().editYourAddress}</p>
                    {selectAddress === mapLocaleToMeaningfulFormat().editLabel ? (
                      <p className="text-xs font-normal">{mapLocaleToMeaningfulFormat().requiredFields}</p>
                    ) : (
                      <></>
                    )}
                  </Label>
                </section>
                {selectAddress === mapLocaleToMeaningfulFormat().editLabel ? (
                  <>
                    <section className="mt-2 flex max-w-[356px] items-center gap-2  bg-almond py-3 text-sm font-medium text-black lg:ml-6">
                      <WarningIcon className="ml-3 h-5 w-5" />
                      {mapLocaleToMeaningfulFormat().verifyEditAddressAlert}
                    </section>
                    <section>
                      <section className="flex w-full gap-x-2 pt-2 lg:px-5">
                        <section className="w-full lg:max-w-[398px]">
                          <InputField
                            items={mapLocaleToMeaningfulFormat().addressLine1}
                            id={
                              editedAddress
                                ? mapLocaleToMeaningfulFormat().editAddressId
                                : mapLocaleToMeaningfulFormat().addressId
                            }
                            htmlFor={
                              editedAddress
                                ? mapLocaleToMeaningfulFormat().editAddressId
                                : mapLocaleToMeaningfulFormat().addressId
                            }
                            register={register}
                            errors={errors}
                            label="streetName"
                            classname="w-full address-input"
                            maxlength={30}
                          />
                        </section>
                        <ToolTips className="mt-5" Content={{ ContentToolTip: <AddressToolTip /> }} />
                      </section>
                      {!showadditionalAddressInfo && (
                        <button
                          type="button"
                          className="flex items-center gap-x-2 pb-5 lg:px-5"
                          onClick={handleShowAdditionalAddressInfo}
                        >
                          <AddCircle className="h-5 w-5" />
                          <p className="text-base font-normal leading-5">
                            {mapLocaleToMeaningfulFormat().addressLine2}
                          </p>
                          <p className="text-sm font-normal leading-5">
                            ({mapLocaleToMeaningfulFormat().optionalText})
                          </p>
                        </button>
                      )}
                      <section
                        className={`${
                          !showadditionalAddressInfo ? 'hidden ' : 'block'
                        } flex w-full items-center pt-2 lg:px-5`}
                      >
                        <section className="w-full lg:max-w-[398px]">
                          <InputField
                            items={mapLocaleToMeaningfulFormat().addressLine2}
                            id={
                              editedAddress
                                ? mapLocaleToMeaningfulFormat().editAddressLine2
                                : mapLocaleToMeaningfulFormat().additionalAddressInfo
                            }
                            htmlFor={
                              editedAddress
                                ? mapLocaleToMeaningfulFormat().editAddressLine2
                                : mapLocaleToMeaningfulFormat().additionalAddressInfo
                            }
                            register={register}
                            errors={errors}
                            label="additionalAddressInfo"
                            classname="w-full addressLine2"
                            maxlength={30}
                          />
                        </section>
                      </section>
                      <section className="flex w-full items-center lg:px-5">
                        <section className="w-full lg:max-w-[398px]">
                          <InputField
                            items={mapLocaleToMeaningfulFormat().city}
                            id={
                              editedAddress
                                ? mapLocaleToMeaningfulFormat().editCity
                                : mapLocaleToMeaningfulFormat().cityId
                            }
                            htmlFor={
                              editedAddress
                                ? mapLocaleToMeaningfulFormat().editCity
                                : mapLocaleToMeaningfulFormat().cityId
                            }
                            register={register}
                            errors={errors}
                            label="city"
                            classname="w-full city"
                            maxlength={30}
                          />
                        </section>
                      </section>
                      <section className="flex gap-x-5 pt-2 lg:w-full lg:px-5">
                        <section className="w-full lg:max-w-[217px]">
                          <StateDropDown
                            setValue={setValue}
                            errors={errors}
                            label="state"
                            classname="w-full state"
                            initialState={values?.state}
                            register={register}
                            id="state"
                          />
                        </section>
                        <section className="w-full max-w-[136px] lg:max-w-[161px]">
                          <InputField
                            items={mapLocaleToMeaningfulFormat().zipCode}
                            id={
                              editedAddress
                                ? mapLocaleToMeaningfulFormat().editZipCode
                                : mapLocaleToMeaningfulFormat().zipCodeId
                            }
                            htmlFor={
                              editedAddress
                                ? mapLocaleToMeaningfulFormat().editZipCode
                                : mapLocaleToMeaningfulFormat().zipCodeId
                            }
                            register={register}
                            errors={errors}
                            label="postalCode"
                            maxlength={10}
                            classname="w-full zip"
                            value={postalCode}
                            onChange={handlePostalCodeInputChange}
                          />
                        </section>
                      </section>
                    </section>
                  </>
                ) : (
                  <></>
                )}

                <section className="flex items-center space-x-2">
                  <RadioGroupItem
                    value="original"
                    id="r3"
                    onClick={() => handleRadioChange(mapLocaleToMeaningfulFormat().originalLabel)}
                  />
                  <Label htmlFor="r3" className="text-base font-semibold">
                    {mapLocaleToMeaningfulFormat().originalAddress}
                  </Label>
                </section>
                {selectAddress === mapLocaleToMeaningfulFormat().originalLabel ? (
                  <section className="ml-6">
                    <section className="mt-2 flex max-w-[356px] items-center gap-2 bg-almond py-3 text-sm font-medium text-black">
                      <WarningIcon className="ml-3 h-5 w-5" />
                      {mapLocaleToMeaningfulFormat().originalAddressAlert}
                    </section>
                    <section className="pt-5">
                      <p>{values?.streetName}</p>
                      <p> {values?.additionalAddressInfo}</p>
                      <p>{values?.city}</p>
                      <p>
                        {' '}
                        {values?.state} {values?.postalCode}
                      </p>
                    </section>
                  </section>
                ) : (
                  <></>
                )}
              </RadioGroup>
            </section>
          ) : (
            <></>
          )}
          <section className="mt-8 flex justify-center">
            <Button
              type="submit"
              onClick={handleSubmit(onSubmitModal)}
              className={`w-[337px] bg-deepBlue py-4 text-lg font-bold leading-7 lg:py-3 lg:text-lg lg:leading-6 ${primaryBtnClass(
                brandName,
              )} ${btnHoverClass(siteId)}`}
            >
              {mapLocaleToMeaningfulFormat().continue}
            </Button>
          </section>
        </form>
      </DialogContent>
    </Dialog>
  );
}
